import React, { useEffect, useRef, useState } from 'react';
import Header from '../../components/header/Header';
import images from '../../constants/images';
import { useTranslation } from 'react-i18next';
import Map from '../../components/map/Map';
import Footer from '../../components/footer/Footer';
import '../../assets/styles/global.css';
import styles from '../about/About.module.css';
import CursorFollower from '../../components/cursor/Cursor';
// import Box from "@mui/material/Box";
// import Stepper from "@mui/material/Stepper";
// import Step from "@mui/material/Step";
// import StepLabel from "@mui/material/StepLabel";
// import StepContent from "@mui/material/StepContent";
// import Button from "@mui/material/Button";
// import Paper from "@mui/material/Paper";
// import Typography from "@mui/material/Typography";
import { motion } from 'framer-motion';
import '../spinner.css';

const OurProcess = () => {
  const steps = [
    {
      image: images.Melting,
      label: 'ourProcess.meltingTitle',
      description: `ourProcess.meltingPara`,
    },
    {
      image: images.BilletCasting,
      label: 'ourProcess.billetTitle',
      description: 'ourProcess.billetPara',
    },
    {
      image: images.Extrusion,
      label: 'ourProcess.extrusionTitle',
      description: `ourProcess.extrusionPara`,
    },
    {
      image: images.Stretching,
      label: 'ourProcess.stretchingTitle',
      description: `ourProcess.stretchingPara`,
    },
    {
      image: images.Hardening,
      label: 'ourProcess.hardeningTitle',
      description: `ourProcess.hardeningPara`,
    },
    {
      image: images.Packaging,
      label: 'ourProcess.packagingTitle',
      description: `ourProcess.packagingPara`,
    },
  ];
  const { t } = useTranslation('global');
  const [isLoading, setIsLoading] = useState(true);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const hasLoadedBefore = sessionStorage.getItem('hasLoadedBefore');

    const timer = setTimeout(
      () => {
        setIsLoading(false);
        sessionStorage.setItem('hasLoadedBefore', 'true'); // mark as loaded
      },
      hasLoadedBefore ? 1000 : 3000,
    ); // shorter delay if already loaded

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowButton(window.scrollY > 410); // Show button after scrolling 200px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleScrollToContact = (event: any) => {
    event.preventDefault(); // Prevent default anchor behavior
    const headerHeight = document.querySelector('header')?.offsetHeight || 0; // Get header height
    const targetPosition = document.getElementById('bottom')?.offsetTop || 0;

    window.scrollTo({
      top: targetPosition - headerHeight, // Subtract the header height
      behavior: 'smooth', // Smooth scrolling effect
    });
  };
  const [activeStep, setActiveStep] = React.useState(0);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!stepRefs.current.length) return;

      let closestStep = 0;
      let minDistance = Infinity;

      stepRefs.current.forEach((step, index) => {
        if (!step) return;
        const rect = step.getBoundingClientRect();
        const distance = Math.abs(rect.top - window.innerHeight / 2);

        if (distance < minDistance) {
          minDistance = distance;
          closestStep = index;
        }
      });

      setActiveStep(closestStep);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    window.addEventListener('scroll', handleScroll);

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const title = t('ourProcess.ourProcessText');
  const words = title.split(' ');

  if (isLoading) {
    return (
      <>
        <div className="loader-wrapper">
          <div className="spinner">
            <div className="cube1"></div>
            <div className="cube2"></div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <CursorFollower />

      {/* Hero Section */}
      <section
        id="hero"
        className="relative top-0 left-0 z-50 font flex flex-col justify-center items-center min-h-screen"
      >
        {/* Header */}
        <Header />

        {/* Background Image */}
        <img
          src={images.ourProcessMain}
          alt="Contact Us Background"
          className="absolute inset-0 object-cover w-screen h-full"
        />

        {/* Overlay */}
        <div className="absolute inset-0"></div>

        {/* Content on top of the image */}
        <div
          className={`relative z-10 flex flex-col justify-between items-center sm:text-center text-left text-white px-4 md:px-48 lg:px-52 2xl:px-64`}
        >
          {/* Responsive Heading */}
          <h1
            className={`title md:text-center text-4xl md:text-4xl lg:text-5xl xl:text-5xl 2xl:text-6xl font-bold pb-4 ${styles.head}`}
          >
            {words.map((word, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, y: 50 }} // Start with invisible and slightly below
                animate={{ opacity: 1, y: 0 }} // Animate to fully visible and centered
                transition={{
                  delay: index * 0.2, // Delay each word animation
                  type: 'spring',
                  stiffness: 100,
                  damping: 25,
                }}
                className="inline-block mr-2" // Add some space between words
              >
                {word}
              </motion.span>
            ))}
          </h1>
        </div>

        {/* Scroll Button: Visible only on screens larger than 'lg' */}
        <div className="hidden lg:flex absolute lg:bottom-0 xl:bottom-0 flex-col items-center">
          <a
            href="#bottom"
            onClick={handleScrollToContact}
            className="flex items-center justify-center color text-white  lg:w-[60px] lg:h-[60px] xl:w-[75px] xl:h-[75px] 2xl:w-[105px] 2xl:h-[105px]  hover:bg-red-500 transition duration-300"
          >
            {/* Icon and Text Container */}
            <div className="flex flex-col items-center">
              {/* Arrow Icon */}
              <img
                src={images.down}
                alt="Down Arrow"
                className="w-4 md:w-5 lg:w-5 xl:w-6 2xl:w-7 h-auto mb-1"
              />
              {/* Scroll Text */}
              <span className="text-xs md:text-sm lg:text-sm xl:text-base 2xl:text-lg">
                {t('heroSection.scroll')}
              </span>
            </div>
          </a>
        </div>

        {/* WhatsApp Floating Button */}
        <a
          href="https://wa.me/919909998890"
          target="_blank"
          rel="noopener noreferrer"
          className={`fixed bottom-10 right-5 sm:right-4 md:right-4 lg:right-8 xl:right-10 bg-green-500 text-white rounded-full p-2 lg:p-3 shadow-lg hover:bg-green-600 transition z-50 ${styles.whatsappBackground}`}
        >
          {/* WhatsApp Image Icon */}
          <img
            src={images.whatsapp_white}
            alt="WhatsApp Icon"
            className={`w-6 lg:w-7 h-auto ${styles.whatsappIcon}`}
          />
        </a>
      </section>

      <section id="bottom" className="relative flex min-h-screen">
        {/* Left Column: Sticky Image (40%) */}
        <div className="hidden md:flex md:w-2/5 2xl:w-3/5 bg-white sticky h-screen items-center justify-center top-14 ">
          <img
            src={steps[activeStep]?.image}
            alt={steps[activeStep]?.label}
            className="w-2/3 h-auto object-contain transition-opacity duration-500 ease-in-out"
            style={{ opacity: activeStep !== -1 ? 1 : 0.5, marginLeft: '96px' }} // Slightly shift image right
          />
        </div>

        {/* Right Column: Scrollable Timeline Content (60%) */}
        <div
          ref={containerRef}
          className="w-full md:w-3/5 overflow-y-auto px-6 md:px-12 py-32  2xl:mt-24 pb-[25vh] 2xl:pb-[30vh] relative bg-transparent"
        >
          {steps.map((step, index) => (
            <div
              key={index}
              ref={(el) => (stepRefs.current[index] = el)}
              className={` relative pl-16 mb-12  2xl:mr-2 ${index === steps.length - 1 ? 'mb-0' : 'pb-12'}`}
              style={{
                opacity: index === activeStep ? 1 : 0.5,
                transform:
                  index === activeStep ? 'translateY(0)' : 'translateY(20px)',
                transition: 'opacity 0.5s ease, transform 0.5s ease',
              }}
            >
              {/* Step Number Box (Sharp Edges, No Border Radius) */}
              <div
                className={`absolute left-4 2xl:left-6 top-0 w-10 h-10 flex items-center justify-center border-2 text-white font-bold ${
                  index === activeStep
                    ? 'color text-white border-red-600 '
                    : 'bg-gray-300 border-gray-300'
                }`}
                style={{ borderRadius: '0px' }} // Sharp square edges
              >
                {index + 1}
              </div>

              {/* Step Content */}
              <div
                style={{
                  maxWidth: '70%', // Restrict description width
                  marginLeft: '20px',
                }}
              >
                <h3
                  style={{
                    fontFamily: "'Nunito Sans'",
                    fontSize: '36px',
                    lineHeight: '44px',
                  }}
                  className={`text-2xl  font-semibold mb-4 text-gray-800`} // Always black font
                >
                  {t(step.label)}
                </h3>
                <p
                  style={{ fontSize: '16px', lineHeight: '24px' }}
                  className="text-gray-600"
                >
                  {t(step.description)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {showButton && (
          <div className="fixed xl:top-1/3 lg:top-2/4 md:top-1/4 top-1/3 right-0 transform z-50 ">
            <div className="py-8 px-3 md:py-6 md:px-6 lg:py-7 xl:py-10 xl:px-16 "></div>
            <div
              className="absolute flex flex-col lg:-right-4 xl:right-0 translate-y-1/2 hidden lg:flex  "
              onClick={scrollToTop}
              style={{ cursor: 'pointer' }}
            >
              <img
                src={images.up}
                alt="UpArrow"
                className={`flex justify-center xl:pb-8 xl:h-16 lg:h-12 lg:pb-6 ${styles.icon}`}
              />
              <button
                onClick={scrollToTop}
                className={`flex text-sm md:text-lg lg:text-sm xl:text-base 2xl:text-lg text-red-500 rotate-90 transition duration-300 ${styles.top}`}
              >
                {t('whoWeAre.top')}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Map Section */}

      <Map />

      {/* Footer Section */}

      <Footer />
    </>
  );
};

export default OurProcess;
